/**
 * Hoop layout for Bamford's Comeback.
 *
 * Based on the original v1 HOOPS array from bamford.html.
 * Coordinates in metres on a scaled court.
 *
 * The real-world distances from v1:
 *   Hoop 9: 3 yards (~2.74m)
 *   Hoop 10: 4 yards (~3.66m)
 *   Hoop 11: 5 yards (~4.57m)
 *   Hoop 12: 5 yards (~4.57m)
 *   Hoop 13: 7 yards (~6.40m)
 *
 * Court origin (0,0) is top-left.
 * Y increases downward (standard canvas convention).
 * Ball starts near the bottom, hoop is near the top.
 * All measurements in metres.
 *
 * Facing angle: hoops face downward (-π/2) so the ball approaches from below.
 */

import type { Vec2 } from '../physics/ball.js'

export interface HoopConfig {
  label: string
  distYards: number
  ballStart: Vec2
  hoopPos: Vec2
  /** Facing angle in radians — which direction the ball must travel to score */
  hoopFacing: number
  commentary: string[]
}

// Court display dimensions (metres)
// A scaled-down representation — real croquet is huge, we scale for display
export const COURT_WIDTH = 20    // metres (display)
export const COURT_HEIGHT = 15   // metres (display)

// All hoops face "upward" — ball shoots from bottom half toward top half
// facing = -π/2 means ball must travel in -y direction to score
const FACING_UP = -Math.PI / 2

export const BAMFORD_HOOPS: HoopConfig[] = [
  {
    label: 'Hoop 9',
    distYards: 3,
    ballStart: { x: 10.3, y: 8.8 },
    hoopPos:   { x: 10, y:  7 },
    hoopFacing: FACING_UP,
    commentary: [
      'The comeback begins.',
      'The crowd stirs.',
      'Bamford steps up to the ball.',
    ],
  },
  {
    label: 'Hoop 10',
    distYards: 4,
    ballStart: { x: 10.7, y: 9.2 },
    hoopPos:   { x: 10.5, y: 7.5 },
    hoopFacing: FACING_UP,
    commentary: [
      '6–4. Nasr\'s lead is shrinking.',
      'Cairo holds its breath.',
      'The Egyptian crowd falls quiet.',
    ],
  },
  {
    label: 'Hoop 11',
    distYards: 5,
    ballStart: { x: 9.7, y: 8.8 },
    hoopPos:   { x: 9.5, y: 7 },
    hoopFacing: FACING_UP,
    commentary: [
      '6–5. One more and it\'s level.',
      'The floodlights flicker on.',
      'The evening call to prayer echoes\nacross the grounds.',
    ],
  },
  {
    label: 'Hoop 12',
    distYards: 5,
    ballStart: { x: 9.8, y: 8.7 },
    hoopPos:   { x: 10, y: 7 },
    hoopFacing: FACING_UP,
    commentary: [
      '6–6. Level.',
      'A phone rings behind Nasr.\nHe flinches.',
      'This is Bamford\'s moment.',
    ],
  },
  {
    label: 'Hoop 13',
    distYards: 7,
    ballStart: { x: 10.2, y: 8.2 },
    hoopPos:   { x: 10, y: 6.5 },
    hoopFacing: FACING_UP,
    commentary: [
      'Six all. The final hoop.',
      'Seven yards from the boundary.',
      'One shot for the World Championship.',
      'The crowd stands.',
    ],
  },
]
